import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';

interface TagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

const COLORS = [
  { name: 'blue', hex: '#3b82f6', class: 'bg-blue-500' },
  { name: 'green', hex: '#10b981', class: 'bg-emerald-500' },
  { name: 'red', hex: '#ef4444', class: 'bg-red-500' },
  { name: 'orange', hex: '#f97316', class: 'bg-orange-500' },
  { name: 'purple', hex: '#8b5cf6', class: 'bg-purple-500' },
  { name: 'pink', hex: '#ec4899', class: 'bg-pink-500' },
  { name: 'indigo', hex: '#6366f1', class: 'bg-indigo-500' },
  { name: 'teal', hex: '#14b8a6', class: 'bg-teal-500' },
];

const TagsModal: React.FC<TagsModalProps> = ({ isOpen, onClose, tenantId }) => {
  const [tags, setTags] = useState<any[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState(COLORS[0]);
  const [loading, setLoading] = useState(false);

  // Carregar etiquetas existentes
  const loadTags = async () => {
    try {
      const res = await axios.get(`https://zapshop.app/api/tags/${tenantId}`);
      setTags(res.data);
    } catch (error) {
      console.error("Erro ao carregar tags", error);
    }
  };

  useEffect(() => {
    if (isOpen) loadTags();
  }, [isOpen, tenantId]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTagName.trim()) return;

    setLoading(true);
    try {
      await axios.post('https://zapshop.app/api/tags', {
        tenant_id: tenantId,
        name: newTagName,
        color: selectedColor.hex
      });
      setNewTagName('');
      loadTags(); // Recarrega a lista
    } catch (error) {
      alert('Erro ao criar etiqueta.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza? A etiqueta sumirá do menu, mas clientes que já a possuem continuarão com ela (sem cor).')) return;
    try {
      await axios.delete(`https://zapshop.app/api/tags/${id}`);
      loadTags();
    } catch (error) {
      alert('Erro ao deletar.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-surface-900 text-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden border border-surface-800" style={{backgroundColor: '#111827', borderColor: '#1f2937'}}>
        <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-900">
          <h3 className="font-bold text-lg text-white">Gerenciar Etiquetas</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20}/></button>
        </div>

        <div className="p-4 space-y-6">
          {/* Formulário de Criação */}
          <form onSubmit={handleCreate} className="space-y-3">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Nova Etiqueta</label>
            <div className="flex gap-2">
              <input 
                autoFocus
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-500"
                placeholder="Ex: Cliente VIP"
                value={newTagName}
                onChange={e => setNewTagName(e.target.value)}
              />
              <button 
                type="submit" 
                disabled={loading}
                className="bg-indigo-600 hover:bg-indigo-500 text-white p-2 rounded-lg transition disabled:opacity-50"
              >
                <Plus size={20} />
              </button>
            </div>
            
            {/* Seletor de Cores */}
            <div className="flex gap-2 flex-wrap">
              {COLORS.map(c => (
                <button
                  key={c.name}
                  type="button"
                  onClick={() => setSelectedColor(c)}
                  className={`w-6 h-6 rounded-full ${c.class} transition-transform hover:scale-110 ${selectedColor.name === c.name ? 'ring-2 ring-white scale-110' : 'opacity-70'}`}
                />
              ))}
            </div>
          </form>

          {/* Lista de Etiquetas Existentes */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Etiquetas Existentes</label>
            <div className="max-h-48 overflow-y-auto pr-1 space-y-2 custom-scrollbar">
              {tags.length === 0 ? (
                <p className="text-sm text-gray-500 italic">Nenhuma etiqueta criada.</p>
              ) : (
                tags.map(tag => (
                  <div key={tag.id} className="flex items-center justify-between bg-gray-800 p-2 rounded-lg border border-gray-700 group">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }}></div>
                      <span className="text-sm font-medium text-gray-200">{tag.name}</span>
                    </div>
                    <button 
                      onClick={() => handleDelete(tag.id)}
                      className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagsModal;
